import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'

// @mui material components
import { Box, Card, CircularProgress, Grid, Tab, Tabs } from '@mui/material'
import moment from 'moment'

// Material Dashboard 2 React components
import MDBox from 'components/MDBox'
import MDButton from 'components/MDButton'
import MDTypography from 'components/MDTypography'

import ConfirmModal from 'components/ConfirmModal/ConfirmModal'
import AccountStatement from './components/AccountStatement'
import DwollaFailureReasonModal from './components/DwollaFailureReasonModal'
import ManualKYC from './components/ManualKYC'
import Properties from './components/Properties'

import { DWOLLA_CUSTOMER_STATUS, DWOLLA_CUSTOMER_STATUS_COLOR } from 'constants/user.constants'
import {
  activateManualKYC,
  approveUsers,
  freezeUserActionDetails,
  getUserDetails,
  resetPinPassword,
  resetUserDetails,
} from 'store/reducers/usersSlice'
import { formatCurrency, formatPhoneNumber, formatSSN } from '../../helpers/formatData'


function TabPanel(props) {
  const { children, value, index, ...other } = props
  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}>
      {value === index && <>{children}</>}
    </div>
  )
}

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  }
}

const UserDetails = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { userId } = useParams()

  const { userDetails } = useSelector((state) => state.usersSlice)

  const [propertyValue, setPropertyValue] = useState('invested')

  const [loading, setLoading] = useState(false)
  const [isUpdatingKYC, setUpdatingKYC] = useState(false)
  const [isAdminUpdatingKYC, setAdminUpdatingKYC] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [isFreezeModal, setFreezeModal] = useState(false)
  const [isFreezeLoading, setIsFreezeLoading] = useState(false)
  const [isReasonModalOpen, setIsReasonModalOpen] = useState(false)

  const [resettingFor, setResettingFor] = useState({
    password: false,
    pin: false,
  })

  // const [value, setValue] = useState(0);
  // const [statements, setStatements] = useState();
  // const handleStatements = (event) => {
  //   const {
  //     target: { value },
  //   } = event;
  //   setStatements(value);
  // };
  // const handleChange = (event, newValue) => {
  //   setValue(newValue);
  // };


  const handlePropertyTabChange = (event, newValue) => {
    setPropertyValue(newValue)
  }

  const onFetchDetailsFail = () => {
    navigate('/users', { replace: true })
  }

  async function fetchUserDetails() {
    try {
      setLoading(true)
      await dispatch(getUserDetails({ userId, onFail: onFetchDetailsFail }))
    } catch (error) {
      console.log({ error })
    } finally {
      setLoading(false)
    }
  }

  const onActivateKYC = async () => {
    try {
      setUpdatingKYC(true)
      await dispatch(activateManualKYC(userId))
    } catch (error) {
      console.log(error)
    } finally {
      setUpdatingKYC(false)
    }
  }

  const onAdminApproveKYC = async () => {
    try {
      setAdminUpdatingKYC(true)
      await dispatch(approveUsers(userId))
    } catch (error) {
      console.log(error)
    } finally {
      setAdminUpdatingKYC(false)
    }
  }
  const onFreezeUserClick = async () => {
    try {
      setIsFreezeLoading(true)
      await dispatch(
        freezeUserActionDetails({
          id: selectedUser?.userId,
          body: {
            isFreeze: !selectedUser?.isFreeze,
          },
        })
      )
    } catch (error) {
      console.log(error, 'error')
      // toast.error()
    } finally {
      setIsFreezeLoading(false)
      onCloseModal()
    }
  }

  const onCloseModal = () => {
    setSelectedUser(false)
    setFreezeModal(false)
  }
  useEffect(() => {
    fetchUserDetails()
  }, [userId])

  useEffect(() => {
    return () => {
      dispatch(resetUserDetails())
    }
  }, [])

  const handleDwollaDashboardClick = (e) => {
    e.stopPropagation()
    window.open(userDetails?.dwollaDashboardURL, '_blank')
  }

  const handleResetPinPassword = (resetFor) => async () => {
    setResettingFor((prevState) => ({ ...prevState, [resetFor]: true }))
    try {
      await dispatch(resetPinPassword({ userId: userId, resetFor }))
    } catch (error) {
      console.log(error)
    } finally {
      setResettingFor((prevState) => ({ ...prevState, [resetFor]: false }))
    }
  }

  const statusColor = () => {
    return DWOLLA_CUSTOMER_STATUS_COLOR[userDetails?.dwollaCustomerStatus] ?? 'info'
  }

  const openReasonsModal = () => setIsReasonModalOpen(true);
  
  const closeReasonsModal = () => setIsReasonModalOpen(false);

  return (
    <MDBox pt={6} pb={3}>
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <Card>
            <MDBox
              mx={2}
              mt={-3}
              py={3}
              px={2}
              variant='gradient'
              bgColor='info'
              borderRadius='lg'
              coloredShadow='info'>
              <MDTypography variant='h6' color='white'>
                Personal Information
              </MDTypography>
            </MDBox>
            {loading ? (
              <Box
                sx={{
                  textAlign: 'center',
                  paddingTop: '40px',
                  paddingBottom: '32px',
                }}>
                <CircularProgress color='info' />
              </Box>
            ) : (
              <Grid mt={3} px={4} pb={3} container>
                <Grid item xs={12} md={3}>
                  <MDTypography variant='h6' fontSize='24px'>
                    {userDetails?.name}
                  </MDTypography>
                  <MDTypography fontSize='large'>{userDetails?.email ?? 'NA'}</MDTypography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <MDBox>
                    <MDTypography variant='span' fontSize='medium'>
                      Contact No:
                    </MDTypography>
                    <MDTypography variant='span' fontSize='medium' color='text'>
                      {' '}
                      {formatPhoneNumber(userDetails?.phoneNumber) ?? 'NA'}
                    </MDTypography>
                  </MDBox>
                  <MDBox>
                    <MDTypography variant='span' fontSize='medium'>
                      State:
                    </MDTypography>
                    <MDTypography variant='span' fontSize='medium' color='text'>
                      {' '}
                      {userDetails?.state ?? 'NA'}
                    </MDTypography>
                  </MDBox>
                  <MDBox>
                    <MDTypography variant='span' fontSize='medium'>
                      City:
                    </MDTypography>
                    <MDTypography variant='span' fontSize='medium' color='text'>
                      {' '}
                      {userDetails?.city ?? 'NA'}
                    </MDTypography>
                  </MDBox>
                  <MDBox>
                    <MDTypography variant='span' fontSize='medium'>
                      Address:
                    </MDTypography>
                    <MDTypography variant='span' fontSize='medium' color='text'>
                      {' '}
                      {userDetails?.address ?? 'NA'}
                    </MDTypography>
                  </MDBox>
                  <MDBox>
                    <MDTypography variant='span' fontSize='medium'>
                      Zip Code:
                    </MDTypography>
                    <MDTypography variant='span' fontSize='medium' color='text'>
                      {' '}
                      {userDetails?.zipCode ?? 'NA'}
                    </MDTypography>
                  </MDBox>
                  <MDBox>
                    <MDTypography variant='span' fontSize='medium'>
                      Date of Birth:
                    </MDTypography>
                    <MDTypography variant='span' fontSize='medium' color='text'>
                      {' '}
                      {userDetails?.dateOfBirth ?? 'NA'}
                    </MDTypography>
                  </MDBox>
                  <MDBox>
                    <MDTypography variant='span' fontSize='medium'>
                      SSN:
                    </MDTypography>
                    <MDTypography variant='span' fontSize='medium' color='text'>
                      {' '}
                      {formatSSN(userDetails?.encodedSSN) ?? 'NA'}
                    </MDTypography>
                  </MDBox>
                  <MDBox>
                    <MDTypography variant='span' fontSize='medium'>
                      Total Wallet Balance:
                    </MDTypography>
                    <MDTypography variant='span' fontSize='medium' color='text'>
                      {' '}
                      {formatCurrency(userDetails?.walletBalance ?? 0) ?? 'NA'}
                    </MDTypography>
                  </MDBox>
                  <MDBox>
                    <MDTypography variant='span' fontSize='medium'>
                      Referral Code:
                    </MDTypography>
                    <MDTypography variant='span' fontSize='medium' color='text'>
                      {' '}
                      {userDetails?.referredBy ?? 'NA'}
                    </MDTypography>
                  </MDBox>
                  <MDBox>
                    <MDTypography variant='span' fontSize='medium'>
                      Referred By User:
                    </MDTypography>
                    <MDTypography variant='span' fontSize='medium' color='text'>
                      {' '}
                      {userDetails?.referredByUser?.name ?? 'NA'}
                    </MDTypography>
                  </MDBox>
                  <MDBox>
                    <MDTypography variant='span' fontSize='medium'>
                    Secondary Listing Agreement:
                    </MDTypography>
                    <MDTypography variant='span' fontSize='medium' color='text'>
                      {' '}
                      {userDetails?.isSecondaryListingAgreementAccepted === true ? 'Accepted' : 'Not Accepted'}
                    </MDTypography>
                  </MDBox>
                </Grid>
                <Grid item xs={12} md={3}>
                  <MDBox>
                    <MDTypography variant='span' fontSize='medium'>
                      Onboarding Stage:
                    </MDTypography>
                    <MDTypography variant='span' fontSize='medium' color='text'>
                      {' '}
                      {userDetails?.onboardingStage && userDetails?.onboardingStage !== 'NA'
                        ? userDetails?.onboardingStage
                        : 'NA'}
                    </MDTypography>
                  </MDBox>
                  <MDBox>
                    <MDTypography variant='span' fontSize='medium'>
                      KYC Approved:
                    </MDTypography>
                    <MDTypography variant='span' fontSize='medium' color='text'>
                      {' '}
                      {userDetails?.accountStatus === 'Active' ? 'Yes' : 'No'}
                    </MDTypography>
                  </MDBox>
                  <MDBox>
                    <MDTypography variant='span' fontSize='medium'>
                      Admin Approved:
                    </MDTypography>
                    <MDTypography variant='span' fontSize='medium' color='text'>
                      {' '}
                      {userDetails?.isAdminApproved === true ? 'Yes' : 'No'}
                    </MDTypography>
                  </MDBox>
                  <MDBox>
                    <MDTypography variant='span' fontSize='medium'>
                      AML Status:
                    </MDTypography>
                    <MDTypography variant='span' fontSize='medium' color='text'>
                      {' '}
                      {userDetails?.amlStatus ?? 'NA'}
                    </MDTypography>
                  </MDBox>
                  <MDBox>
                    <MDTypography variant='span' fontSize='medium'>
                      Investment Status:
                    </MDTypography>
                    <MDTypography variant='span' fontSize='medium' color='text'>
                      {' '}
                      {userDetails?.investmentStatus ?? 'NA'}
                    </MDTypography>
                  </MDBox>
                  <MDBox>
                    <MDTypography variant='span' fontSize='medium'>
                      Total Investments:
                    </MDTypography>
                    <MDTypography variant='span' fontSize='medium' color='text'>
                      {' '}
                      {formatCurrency(userDetails?.propertyValue) ?? 'NA'}
                    </MDTypography>
                  </MDBox>
                  <MDBox>
                    <MDTypography variant='span' fontSize='medium'>
                      Total Properties Invested:
                    </MDTypography>
                    <MDTypography variant='span' fontSize='medium' color='text'>
                      {' '}
                      {userDetails?.totalProperties ?? 'NA'}
                    </MDTypography>
                  </MDBox>
                  <MDBox>
                    <MDTypography variant='span' fontSize='medium'>
                      Regulation D Status:
                    </MDTypography>
                    <MDTypography variant='span' fontSize='medium' color='text'>
                      {' '}
                      {userDetails?.isRegDInvestor ?? 'NA'}
                    </MDTypography>
                  </MDBox>
                  <MDBox>
                    <MDTypography variant='span' fontSize='medium'>
                      Registered On:
                    </MDTypography>
                    <MDTypography variant='span' fontSize='medium' color='text'>
                      {' '}
                      {moment(userDetails?.registeredOn ?? 'NA').format('MM/DD/YYYY')}
                    </MDTypography>
                  </MDBox>
                  <MDBox>
                    <MDTypography variant='span' fontSize='medium'>
                      Sign-up Method:
                    </MDTypography>
                    <MDTypography  variant='span' fontSize='medium' color='text'>
                      {` `}{userDetails?.signupMethod ?? 'NA'}
                    </MDTypography>
                  </MDBox>
                  <MDBox>
                    <MDTypography variant='span' fontSize='medium'>
                      Mobile App Version:
                    </MDTypography>
                    <MDTypography  variant='span' fontSize='medium' color='text'>
                      {` `}{userDetails?.mobileAppVersion ?? 'NA'}
                    </MDTypography>
                  </MDBox>
                  <MDBox>
                    <MDTypography variant='span' fontSize='medium'>
                      Action :
                    </MDTypography>{' '}
                    <MDTypography
                      variant='span'
                      fontSize='medium'
                      color='info'
                      sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                      onClick={(e) => {
                        e.stopPropagation()
                        setSelectedUser(userDetails)
                        setFreezeModal(true)
                      }}>
                      {userDetails?.isFreeze ? 'Unfreeze' : 'Freeze'}
                    </MDTypography>
                  </MDBox>
                  {userDetails?.dwollaCustomerStatus &&
                    <MDBox>
                      <MDTypography variant='span' fontSize='medium'>
                        Dwolla Customer Status :
                      </MDTypography>
                      {` `}
                      <MDTypography
                        variant='span'
                        fontSize='medium'
                        color={statusColor()}
                        sx={{ textTransform: 'capitalize' }}>
                        {userDetails?.dwollaCustomerStatus}
                      </MDTypography>
                      {
                        userDetails?.dwollaCustomerStatus === DWOLLA_CUSTOMER_STATUS.DOCUMENT && (
                          <>
                            {` - `}
                            <MDTypography
                              variant='span'
                              fontSize='medium'
                              color='info'
                              sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                              onClick={openReasonsModal}>
                              Reasons
                            </MDTypography>
                          </>
                        )
                      }
                    
                    </MDBox>
                  }
                  {userDetails?.dwollaDashboardURL?.length && (
                      <MDBox>
                        <MDTypography variant='span' fontSize='medium'>
                          Dwolla Document Status :
                        </MDTypography>
                        {` `}
                        <MDTypography
                          variant='span'
                          fontSize='medium'
                          color='info'
                          sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                          onClick={handleDwollaDashboardClick}>
                          Dwolla Dashboard
                        </MDTypography>
                      </MDBox>
                     )}
                  {isFreezeModal && (
                    <ConfirmModal
                      open={isFreezeModal}
                      onOkayClick={onFreezeUserClick}
                      onCloseModal={onCloseModal}
                      modalText={`Are you sure you want to ${selectedUser?.isFreeze ? 'unfreeze' : 'freeze'
                        } this user?`}
                      okayText={`Yes, ${selectedUser?.isFreeze ? 'Unfreeze' : 'Freeze'}`}
                      cancelText='No, cancel'
                      isLoading={isFreezeLoading}
                    />
                  )}
                </Grid>
                <Grid item xs={12} md={3}>
                  <MDTypography variant='h6' mt={1}>
                    Last Login Details
                  </MDTypography>
                  <MDBox>
                    <MDTypography variant='span' fontSize='medium'>
                      Date :
                    </MDTypography>
                    <MDTypography variant='span' fontSize='medium' color='text'>
                      {' '}
                      {userDetails?.lastSession && userDetails?.lastSession !== 'NA'
                        ? new Date(+userDetails?.lastSession).toLocaleString()
                        : 'NA'}
                    </MDTypography>
                  </MDBox>
                  <MDBox>
                    <MDTypography variant='span' fontSize='medium'>
                      IP Address:
                    </MDTypography>
                    <MDTypography variant='span' fontSize='medium' color='text'>
                      {' '}
                      {userDetails?.ipAddress ?? 'NA'}
                    </MDTypography>
                  </MDBox>
                  <MDBox>
                    <MDTypography variant='span' fontSize='medium'>
                      City:
                    </MDTypography>
                    <MDTypography variant='span' fontSize='medium' color='text'>
                      {' '}
                      {userDetails?.ipCity ?? 'NA'}
                    </MDTypography>
                  </MDBox>
                  <MDBox>
                    <MDTypography variant='span' fontSize='medium'>
                      State:
                    </MDTypography>
                    <MDTypography variant='span' fontSize='medium' color='text'>
                      {' '}
                      {userDetails?.ipState ?? 'NA'}
                    </MDTypography>
                  </MDBox>
                  <MDBox>
                    <MDTypography variant='span' fontSize='medium'>
                      Country:
                    </MDTypography>
                    <MDTypography variant='span' fontSize='medium' color='text'>
                      {' '}
                      {userDetails?.ipCountry ?? 'NA'}
                    </MDTypography>
                  </MDBox>
                  <MDBox>
                    <MDTypography variant='span' fontSize='medium'>
                      Latitude:
                    </MDTypography>
                    <MDTypography variant='span' fontSize='medium' color='text'>
                      {' '}
                      {userDetails?.ipLatitude ?? 'NA'}
                    </MDTypography>
                  </MDBox>
                  <MDBox>
                    <MDTypography variant='span' fontSize='medium'>
                      Longitude:
                    </MDTypography>
                    <MDTypography variant='span' fontSize='medium' color='text'>
                      {' '}
                      {userDetails?.ipLongitude ?? 'NA'}
                    </MDTypography>
                  </MDBox>
                </Grid>
                <Grid item xs={12}>
                  <MDBox mt={2} sx={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
                    <MDButton
                      variant='gradient'
                      color='info'
                      onClick={handleResetPinPassword('password')}
                      isLoading={resettingFor?.password}
                      disabled={resettingFor?.password}>
                      Reset Password
                    </MDButton>
                    <MDButton
                      variant='gradient'
                      color='info'
                      onClick={handleResetPinPassword('pin')}
                      isLoading={resettingFor?.pin}
                      disabled={resettingFor?.pin}>
                      Reset Pin
                    </MDButton>
                  </MDBox>
                </Grid>
              </Grid>
            )}
          </Card>

          {isReasonModalOpen &&
            <DwollaFailureReasonModal isOpen={isReasonModalOpen}
              onClose={closeReasonsModal}
              dwollaVerificationFailureReasons={userDetails?.dwollaVerificationFailureReasons}
            />
          }

          <ManualKYC
            isLoading={loading}
            documentType={userDetails?.documentType}
            isKYCVerified={userDetails?.isKYCVerified}
            isAdminApproved={userDetails?.isAdminApproved}
            frontUrl={userDetails?.frontUrl ?? ''}
            backUrl={userDetails?.backUrl ?? ''}
            isUpdatingKYC={isUpdatingKYC}
            onActivateKYC={onActivateKYC}
            onAdminApproveKYC={onAdminApproveKYC}
          />

          <Card sx={{ marginTop: '64px' }}>
            <MDBox
              mx={2}
              mt={-3}
              py={3}
              px={2}
              variant='gradient'
              bgColor='info'
              borderRadius='lg'
              coloredShadow='info'>
              <MDTypography variant='h6' color='white'>
                Properties
              </MDTypography>
            </MDBox>
            <MDBox mt={3} mb={0}>
              <Tabs
                sx={{ padding: '16px 20px' }}
                value={propertyValue}
                onChange={handlePropertyTabChange}
                aria-label='basic tabs example'>
                <Tab label='Invested' value={'invested'} {...a11yProps(0)} />
                <Tab label='Saved' value={'saved'} {...a11yProps(1)} />
              </Tabs>
            </MDBox>
            <TabPanel value={propertyValue} index={'invested'}>
              <Properties activeTab={propertyValue} />
            </TabPanel>
            <TabPanel value={propertyValue} index={'saved'}>
              <Properties activeTab={propertyValue} />
            </TabPanel>
          </Card>
          {/* TODO: To be implemented in future sprints */}
          <Card sx={{ marginTop: '64px' }}>
            <MDBox
              mx={2}
              mt={-3}
              py={3}
              px={2}
              variant='gradient'
              bgColor='info'
              borderRadius='lg'
              coloredShadow='info'>
              <MDTypography variant='h6' color='white'>
                Legal - Account Statements
              </MDTypography>
            </MDBox>
            <AccountStatement userId={userId} />
            {/* <MDBox mt={3} mb={0}>
              <Tabs
                sx={{ padding: '16px 20px' }}
                value={value}
                onChange={handleChange}
                aria-label="basic tabs example"
              >
                <Tab label="Taxes" {...a11yProps(0)} />
                <Tab label="Account Statement" {...a11yProps(0)} />
              </Tabs>
            </MDBox> */}
            {/* <TabPanel value={value} index={0}>
              <MDBox
                sx={{
                  paddingBottom: {
                    xs: '150px',
                    sm: '95px',
                  },
                }}
              >
                <DataTable
                  noBorders
                  table={{ columns: taxcolumns, rows: taxrows }}
                  entriesPerPage={false}
                  canSearch
                />
              </MDBox>
            </TabPanel> */}
            {/* <TabPanel value={value} index={0}> */}

            {/* </TabPanel> */}
          </Card>
        </Grid>
      </Grid>
    </MDBox>
  );
};

export default UserDetails
